import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import { orgSelectors } from 'ducks/orgs'
import { useTaxation } from 'hooks/useTaxation'
import { BaseTheme, IconButton, styled, Typography } from 'opensolar-ui'
import { forwardRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { BundleRow, ProductBundleRow } from './ProductBundleRow'
import { useBundleDeal } from './hooks/useBundleDeal'
import { Deal } from './types'

const Header = styled(MuiDialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
})

const Content = styled(MuiDialogContent)({
  padding: '0 24px 24px',
})

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  borderTop: '1px solid #E0E0E0',
})

const AddAllButton = styled('button')<{ disabled?: boolean; added?: boolean }>(({ theme, disabled, added }) => ({
  padding: '10px 24px',
  backgroundColor: added ? BaseTheme.palette.green[900] : disabled ? '#ccc' : theme.palette.blue?.[900] || '#1751D0',
  color: added ? '#fff' : disabled ? '#666' : '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: added ? 'default' : disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  '&:hover': {
    opacity: added || disabled ? 1 : 0.9,
  },
}))

const SavingsBadge = styled('span')({
  display: 'inline-block',
  background: '#E8F5E9',
  color: '#2E7D32',
  fontWeight: 700,
  fontSize: 13,
  borderRadius: 4,
  padding: '4px 10px',
  whiteSpace: 'nowrap',
})

const FooterPriceBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

export interface BundleDealModalProps {
  open: boolean
  onClose: () => void
  deal: Deal
  currentProductCode?: string
}

const BundleDealModal = forwardRef<HTMLDivElement, BundleDealModalProps>(
  ({ open, onClose, deal, currentProductCode }, ref) => {
    const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
    const distributorKey = deal.distributor
    const {
      isFetching,
      bundleCodes,
      sortedProducts,
      productsToAdd,
      selectedProducts,
      toggleProduct,
      allowSelection,
      otherLabel,
      hasOutOfStock,
      bundleTotal,
      savings,
      discountedTotal,
      allInCart,
      addBundleToCart,
    } = useBundleDeal({ deal, currentProductCode })

    const handleConfirm = () => {
      if (allInCart || hasOutOfStock || !productsToAdd.length) return
      addBundleToCart()
      onClose()
    }

    const discountLabel = useMemo(() => {
      const { action } = deal
      if (action.discount_type === 'percentage') return `${action.discount_value}% off when bought together`
      if (action.discount_type === 'fixed_amount')
        return `${currencySymbol}${action.discount_value} off when bought together`
      return deal.title
    }, [deal, currencySymbol])

    const itemCount = productsToAdd.length

    const handleKeyDown = (e: React.KeyboardEvent) => {
      e.stopPropagation()
    }
    const countryCode = useSelector(orgSelectors.getOrgIso2)
    const { taxName } = useTaxation(countryCode)

    return (
      <Dialog ref={ref} open={open} onClose={onClose} maxWidth="lg" fullWidth onKeyDown={handleKeyDown}>
        <Header disableTypography>
          <div>
            <Typography variant="h5">{deal.title}</Typography>
            <Typography variant="body2" style={{ color: '#018030', fontWeight: 600, marginTop: 4 }}>
              {discountLabel}
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
            {allowSelection
              ? 'Select the products you want and add them to your cart.'
              : 'Add all bundle products to your cart to receive the discount.'}
          </Typography>
          <BundleRow container wrap="nowrap">
            <ProductBundleRow
              products={sortedProducts}
              isFetching={isFetching}
              bundleCodes={bundleCodes}
              distributorKey={distributorKey}
              currentProductCode={currentProductCode}
              otherLabel={otherLabel}
              allowSelection={allowSelection}
              selectedProducts={selectedProducts}
              onToggleProduct={toggleProduct}
            />
          </BundleRow>
        </Content>
        <Footer>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Total bundle price ({itemCount} item{itemCount !== 1 ? 's' : ''})
          </Typography>
          {bundleTotal > 0 && (
            <FooterPriceBlock>
              {savings > 0 && (
                <Typography variant="body2" style={{ textDecoration: 'line-through', color: '#999' }}>
                  {currencySymbol}
                  {bundleTotal.toFixed(2)}
                </Typography>
              )}
              <Typography variant="h5" style={{ color: savings > 0 ? '#018030' : undefined, fontWeight: 700 }}>
                {currencySymbol}
                {discountedTotal.toFixed(2)}
              </Typography>
              {taxName.length > 0 && (
                <Typography variant="caption" style={{ color: '#999' }}>
                  inc. {taxName}
                </Typography>
              )}
            </FooterPriceBlock>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {savings > 0 && (
              <SavingsBadge>
                Save {currencySymbol}
                {savings.toFixed(2)}
              </SavingsBadge>
            )}
            <div style={{ textAlign: 'right' }}>
              {hasOutOfStock && (
                <Typography variant="caption" style={{ color: '#d32f2f', display: 'block', marginBottom: 4 }}>
                  Some products are out of stock
                </Typography>
              )}
              <AddAllButton
                onClick={handleConfirm}
                disabled={isFetching || !productsToAdd.length || hasOutOfStock}
                added={allInCart}
              >
                {allInCart ? (
                  <>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Added to cart
                  </>
                ) : allowSelection ? (
                  'Add selected to cart'
                ) : (
                  'Add all to cart'
                )}
              </AddAllButton>
            </div>
          </div>
        </Footer>
      </Dialog>
    )
  }
)

BundleDealModal.displayName = 'BundleDealModal'

export default BundleDealModal
